// src/pages/purchase/ListaCompra.js
// Lista de compras con paginación, filtros y modal nueva compra

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Table, Spinner, Alert, Form, Pagination } from 'react-bootstrap';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import NuevaCompraModal from '../../components/purchase/NuevaCompraModal';
import DetalleCompraModal from '../../components/purchase/DetalleCompraModal';
import RegistrarStockModal from '../../components/purchase/RegistrarStockModal';
import MarcarPagadaModal from '../../components/purchase/MarcarPagadaModal';
import {
  listPurchases,
  getPurchaseReportMonth,
  getPurchaseReportPending,
  getPurchaseReportOverdue
} from '../../services/purchaseApi';
import { dotoniwoofApi } from '../../api/dotoniwooApi';
import { getDueDate } from '../../utils/purchaseUtils';
import './ListaCompra.css';

const formatterCOP = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const ListaCompra = ({ initialShowModal = false }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [showModal, setShowModal] = useState(initialShowModal);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [showMarcarPagadaModal, setShowMarcarPagadaModal] = useState(false);
  const [selectedPurchaseId, setSelectedPurchaseId] = useState(null);
  const [stockPurchaseId, setStockPurchaseId] = useState(null);
  const [purchaseToMarkPaid, setPurchaseToMarkPaid] = useState(null);
  const [purchaseToEdit, setPurchaseToEdit] = useState(null);
  const limit = 10;

  // Reportes (cards de resumen)
  const [reportMonth, setReportMonth] = useState(0);
  const [reportPending, setReportPending] = useState(0);
  const [reportOverdue, setReportOverdue] = useState(0);

  // Filtro de estado: 'all' | 'PENDIENTE_DE_PAGO' | 'PAGADA' | 'overdue'
  const [statusFilter, setStatusFilter] = useState('all');

  // Filtros (valores en UI)
  const [supplierId, setSupplierId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState('');
  // Filtros aplicados (al hacer clic en Filtrar)
  const [appliedFilters, setAppliedFilters] = useState({});
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [paymentOptions, setPaymentOptions] = useState([]);

  const hasFilters =
    appliedFilters.supplierId ||
    appliedFilters.dateFrom ||
    appliedFilters.dateTo ||
    appliedFilters.paymentMethodId;

  const loadReports = useCallback(async () => {
    const now = new Date();
    const [monthRes, pendingRes, overdueRes] = await Promise.all([
      getPurchaseReportMonth(now.getFullYear(), now.getMonth() + 1),
      getPurchaseReportPending(),
      getPurchaseReportOverdue()
    ]);
    const toNum = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
    setReportMonth(toNum(monthRes.total));
    setReportPending(toNum(pendingRes.total));
    setReportOverdue(toNum(overdueRes.total));
  }, []);

  const loadOptions = useCallback(async () => {
    try {
      const [suppRes, payRes] = await Promise.all([
        dotoniwoofApi.get('/supplier.php').catch(() => dotoniwoofApi.get('/supply.php')),
        dotoniwoofApi.get('/paymentMethod.php').catch(() => dotoniwoofApi.get('/paymentMethod.php/purchase'))
      ]);
      if (suppRes?.data?.code === 200) setSupplierOptions(suppRes.data.message || []);
      if (payRes?.data?.code === 200) setPaymentOptions(payRes.data.message || []);
    } catch (e) {
      console.warn('Error cargando opciones de filtro', e);
    }
  }, []);

  const loadPurchases = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    const f = appliedFilters;
    const filters = {};
    if (f.supplierId) filters.supplier_id = f.supplierId;
    if (f.dateFrom) filters.date_from = f.dateFrom;
    if (f.dateTo) filters.date_to = f.dateTo;
    if (f.paymentMethodId) filters.payment_method_id = f.paymentMethodId;
    if (statusFilter === 'PENDIENTE_DE_PAGO' || statusFilter === 'PAGADA') filters.status = statusFilter;
    if (statusFilter === 'overdue') filters.overdue = 1;

    const fetchLimit =
      f.supplierId || f.dateFrom || f.dateTo || f.paymentMethodId || statusFilter !== 'all'
        ? 500
        : limit;
    const { ok, items: data, total_pages, total_rows } = await listPurchases(page, fetchLimit, filters);

    if (ok) {
      const list = data || [];
      let filtered = list;
      const hasExtraFilters = f.supplierId || f.dateFrom || f.dateTo || f.paymentMethodId;
      if (hasExtraFilters || statusFilter !== 'all') {
        filtered = list.filter((p) => {
          if (f.supplierId && String(p.id_supplier) !== String(f.supplierId)) return false;
          if (f.dateFrom && (p.purchase_date || '') < f.dateFrom) return false;
          if (f.dateTo && (p.purchase_date || '') > f.dateTo) return false;
          if (f.paymentMethodId && String(p.id_payment_method) !== String(f.paymentMethodId))
            return false;
          if (statusFilter === 'PENDIENTE_DE_PAGO') {
            const s = (p.status || p.status_name || '').toUpperCase();
            if (s !== 'PENDIENTE_DE_PAGO') return false;
          }
          if (statusFilter === 'PAGADA') {
            const s = (p.status || p.status_name || '').toUpperCase();
            if (s !== 'PAGADA') return false;
          }
          if (statusFilter === 'overdue') {
            const s = (p.status || p.status_name || '').toUpperCase();
            const dueDate = getDueDate(p);
            const overdue =
              p.overdue === true ||
              (s === 'PENDIENTE_DE_PAGO' && dueDate && new Date(dueDate) < new Date());
            if (!overdue) return false;
          }
          return true;
        });
      }
      const totalFiltered = filtered.length;
      const useClientPagination = hasFilters || statusFilter !== 'all';
      const totalP = useClientPagination
        ? Math.max(1, Math.ceil(totalFiltered / limit))
        : (total_pages || 1);
      const start = (page - 1) * limit;
      const paginated = useClientPagination ? filtered.slice(start, start + limit) : filtered;
      setItems(paginated);
      setTotalPages(totalP);
      setTotalRows(hasFilters ? totalFiltered : (total_rows ?? list.length));
    } else {
      setErrorMsg('No se pudo cargar la lista de compras.');
    }
    setLoading(false);
  }, [page, appliedFilters, statusFilter, hasFilters]);

  useEffect(() => {
    loadOptions();
    loadReports();
  }, [loadOptions, loadReports]);

  useEffect(() => {
    loadPurchases();
  }, [loadPurchases]);

  const handleApplyFilters = () => {
    setAppliedFilters({
      supplierId: supplierId || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      paymentMethodId: paymentMethodId || undefined
    });
    setPage(1);
  };

  const handleClearFilters = () => {
    setSupplierId('');
    setDateFrom('');
    setDateTo('');
    setPaymentMethodId('');
    setAppliedFilters({});
    setPage(1);
  };

  const getStatusBadgeClass = (purchase) => {
    const status = (purchase?.status || purchase?.status_name || '').toUpperCase();
    const dueDate = getDueDate(purchase);
    const isOverdue =
      status === 'PENDIENTE_DE_PAGO' && dueDate && new Date(dueDate) < new Date();
    if (isOverdue) return 'vencida';
    if (status === 'PAGADA') return 'pagada';
    if (status === 'PENDIENTE_DE_PAGO') return 'pendiente';
    if (status === 'ACTIVO' || status === 'COMPLETO') return 'pagada';
    return 'pendiente';
  };

  const isOverdue = (purchase) => {
    const status = (purchase?.status || purchase?.status_name || '').toUpperCase();
    if (status !== 'PENDIENTE_DE_PAGO') return false;
    const dueDate = getDueDate(purchase);
    return dueDate && new Date(dueDate) < new Date();
  };

  const canMarkAsPaid = (purchase) => {
    const status = (purchase?.status || purchase?.status_name || '').toUpperCase();
    return status === 'PENDIENTE_DE_PAGO';
  };

  const refreshAll = useCallback(() => {
    loadPurchases();
    loadReports();
  }, [loadPurchases, loadReports]);

  return (
    <>
      <NavContainer />
      <OffCanvasNav />
      <Container fluid className="lista-compra-container">
        <Row>
          <Col>
            <Card className="lista-compra-card">
              <div className="lista-compra-header">
                <h1 className="lista-compra-title">Compras</h1>
                <Button className="lista-compra-btn-new" onClick={() => setShowModal(true)}>
                  Nueva compra
                </Button>
              </div>

              <div className="lista-compra-summary-cards">
                <div className="lista-compra-summary-card">
                  <span className="lista-compra-summary-label">Total del mes</span>
                  <span className="lista-compra-summary-value">
                    {formatterCOP.format(reportMonth)}
                  </span>
                </div>
                <div className="lista-compra-summary-card">
                  <span className="lista-compra-summary-label">Pendiente por pagar</span>
                  <span className="lista-compra-summary-value lista-compra-summary-warning">
                    {formatterCOP.format(reportPending)}
                  </span>
                </div>
                <div className="lista-compra-summary-card">
                  <span className="lista-compra-summary-label">Vencido</span>
                  <span className="lista-compra-summary-value lista-compra-summary-danger">
                    {formatterCOP.format(reportOverdue)}
                  </span>
                </div>
              </div>

              <div className="lista-compra-status-tabs">
                <button
                  type="button"
                  className={`lista-compra-tab ${statusFilter === 'all' ? 'active' : ''}`}
                  onClick={() => {
                    setStatusFilter('all');
                    setPage(1);
                  }}
                >
                  Todas
                </button>
                <button
                  type="button"
                  className={`lista-compra-tab ${statusFilter === 'PENDIENTE_DE_PAGO' ? 'active' : ''}`}
                  onClick={() => {
                    setStatusFilter('PENDIENTE_DE_PAGO');
                    setPage(1);
                  }}
                >
                  Pendientes
                </button>
                <button
                  type="button"
                  className={`lista-compra-tab ${statusFilter === 'PAGADA' ? 'active' : ''}`}
                  onClick={() => {
                    setStatusFilter('PAGADA');
                    setPage(1);
                  }}
                >
                  Pagadas
                </button>
                <button
                  type="button"
                  className={`lista-compra-tab ${statusFilter === 'overdue' ? 'active' : ''}`}
                  onClick={() => {
                    setStatusFilter('overdue');
                    setPage(1);
                  }}
                >
                  Vencidas
                </button>
              </div>

              <div className="lista-compra-filters">
                <div className="lista-compra-filters-inner">
                  <div className="lista-compra-filter-group">
                    <label>Proveedor</label>
                    <Form.Select
                      value={supplierId}
                      onChange={(e) => setSupplierId(e.target.value)}
                    >
                      <option value="">Todos</option>
                      {supplierOptions.map((s) => (
                        <option key={s.id_supplier || s.id} value={s.id_supplier || s.id}>
                          {s.name || s.supplier_name}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="lista-compra-filter-group">
                    <label>Desde</label>
                    <Form.Control
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </div>
                  <div className="lista-compra-filter-group">
                    <label>Hasta</label>
                    <Form.Control
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </div>
                  <div className="lista-compra-filter-group">
                    <label>Método de pago</label>
                    <Form.Select
                      value={paymentMethodId}
                      onChange={(e) => setPaymentMethodId(e.target.value)}
                    >
                      <option value="">Todos</option>
                      {paymentOptions.map((p) => (
                        <option key={p.id_payment_method || p.id} value={p.id_payment_method || p.id}>
                          {p.method_name || p.name}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="lista-compra-filter-actions">
                    <Button className="lista-compra-btn-filter" onClick={handleApplyFilters}>
                      Filtrar
                    </Button>
                    {hasFilters && (
                      <Button variant="outline-secondary" className="lista-compra-btn-clear" onClick={handleClearFilters}>
                        Limpiar
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <Card.Body className="lista-compra-body">
                {errorMsg && (
                  <Alert variant="danger" dismissible onClose={() => setErrorMsg('')}>
                    {errorMsg}
                  </Alert>
                )}
                {loading ? (
                  <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" style={{ color: '#EA9D0E' }} />
                  </div>
                ) : (
                  <>
                    <Table striped hover responsive className="lista-compra-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Fecha</th>
                          <th>Proveedor</th>
                          <th>Referencia</th>
                          <th>Total</th>
                          <th>Método de pago</th>
                          <th>Estado</th>
                          <th>Vencimiento</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="text-center py-5 text-muted">
                              {hasFilters || statusFilter !== 'all'
                                ? 'No hay compras que coincidan con los filtros.'
                                : 'No hay compras registradas.'}
                            </td>
                          </tr>
                        ) : (
                          items.map((p) => (
                            <tr key={p.id_purchase}>
                              <td>{p.id_purchase}</td>
                              <td>{p.purchase_date || '-'}</td>
                              <td>{p.supplier_name || p.supplier?.name || '-'}</td>
                              <td>{p.reference_doc || '-'}</td>
                              <td>{formatterCOP.format(p.grand_total ?? 0)}</td>
                              <td>{p.method_name || '-'}</td>
                              <td>
                                <span className={`lista-compra-badge ${getStatusBadgeClass(p)}`}>
                                  {p.status || p.status_name || 'pendiente'}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={
                                    isOverdue(p) ? 'lista-compra-due-date overdue' : ''
                                  }
                                >
                                  {getDueDate(p) || '-'}
                                </span>
                              </td>
                              <td>
                                <div className="lista-compra-actions">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="btn-detail"
                                    onClick={() => {
                                      setSelectedPurchaseId(p.id_purchase);
                                      setShowDetailModal(true);
                                    }}
                                  >
                                    Ver detalle
                                  </Button>
                                  <Button
                                    variant="outline-warning"
                                    size="sm"
                                    className="btn-edit"
                                    onClick={() => {
                                      setPurchaseToEdit(p.id_purchase);
                                      setShowModal(true);
                                    }}
                                  >
                                    Editar
                                  </Button>
                                  {canMarkAsPaid(p) && (
                                    <Button
                                      variant="outline-success"
                                      size="sm"
                                      className="btn-marcar-pagada"
                                      onClick={() => {
                                        setPurchaseToMarkPaid({
                                          id: p.id_purchase,
                                          reference: p.reference_doc
                                        });
                                        setShowMarcarPagadaModal(true);
                                      }}
                                    >
                                      Marcar pagada
                                    </Button>
                                  )}
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="btn-stock"
                                    onClick={() => {
                                      setStockPurchaseId(p.id_purchase);
                                      setShowStockModal(true);
                                    }}
                                  >
                                    Registrar stock
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>

                    {totalPages > 0 && (
                      <div className="lista-compra-footer">
                        <span className="page-info">
                          {totalRows} compra{totalRows !== 1 ? 's' : ''}
                        </span>
                        <Pagination className="lista-compra-pagination mb-0">
                          <Pagination.Prev
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          />
                          <Pagination.Item active>{page} / {totalPages}</Pagination.Item>
                          <Pagination.Next
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          />
                        </Pagination>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <NuevaCompraModal
          show={showModal}
          onHide={() => {
            setShowModal(false);
            setPurchaseToEdit(null);
          }}
          purchaseId={purchaseToEdit}
          onSuccess={(id) => {
            const wasEditMode = !!purchaseToEdit;
            refreshAll();
            setPurchaseToEdit(null);
            if (id && !wasEditMode) {
              // Solo abrir detalle si es una compra nueva
              setSelectedPurchaseId(id);
              setShowDetailModal(true);
            }
          }}
        />
        <DetalleCompraModal
          show={showDetailModal}
          onHide={() => {
            setShowDetailModal(false);
            setSelectedPurchaseId(null);
          }}
          purchaseId={selectedPurchaseId}
          onRefresh={refreshAll}
          onRegistrarStock={(id) => {
            setShowDetailModal(false);
            setSelectedPurchaseId(null);
            setStockPurchaseId(id);
            setShowStockModal(true);
          }}
        />
        <RegistrarStockModal
          show={showStockModal}
          onHide={() => {
            setShowStockModal(false);
            setStockPurchaseId(null);
          }}
          purchaseId={stockPurchaseId}
          onSuccess={refreshAll}
        />
        <MarcarPagadaModal
          show={showMarcarPagadaModal}
          onHide={() => {
            setShowMarcarPagadaModal(false);
            setPurchaseToMarkPaid(null);
          }}
          purchaseId={purchaseToMarkPaid?.id}
          purchaseReference={purchaseToMarkPaid?.reference}
          onSuccess={refreshAll}
        />
      </Container>
    </>
  );
};

export default ListaCompra;
